// ═════════════════════════════════════════════════════════════════════════════
//  EL OÍDO DE SPACY COACH
//
//  ── POR QUE UNO PROPIO Y NO EL DEL GLOSARIO ──────────────────────────────────
//  NSL ya escucha gratis en seis sitios (`useMicSystem`, `webSpeechUtils`, los
//  motores, pronunciación), pero todos usan `lang = 'en-US'` porque están para
//  practicar inglés: el alumno lee una frase en inglés y se comprueba cómo la
//  dijo.
//
//  Con el coach pasa lo contrario. El alumno le habla en ESPAÑOL —«ya terminé»,
//  «no me acuerdo», «hoy no puedo»— con alguna palabra en inglés metida dentro.
//  Escuchar eso en `en-US` devuelve un destrozo. Por eso este oído va en
//  `es-MX`, que es el único cambio de fondo, pero es el que decide si el coach
//  entiende o no.
//
//  ── NO CUESTA NADA ───────────────────────────────────────────────────────────
//  Es el reconocimiento del propio navegador. Cero dólares por minuto, frente a
//  los ~$0.0092 del socket de streaming que se descartó. Su límite es que no
//  existe en Firefox y que en Chrome el audio pasa por los servidores de
//  Google; por eso `soportado` se expone y la pantalla ofrece escribir cuando
//  no lo hay.
//
//  ── LO QUE OYE SE ENSEÑA, NO SE ENVÍA ────────────────────────────────────────
//  El texto reconocido va a la caja para que el alumno lo VEA y pueda
//  corregirlo antes de mandarlo. Una transcripción mala que se envía sola
//  convierte un fallo del micrófono en un coach que parece tonto, y el alumno
//  no tiene forma de saber cuál de las dos cosas pasó.
// ═════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useRef, useState } from "react";

function Reconocimiento() {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

export function useOidoCoach({ onTexto, onError } = {}) {
  const [escuchando, setEscuchando] = useState(false);
  const [parcial, setParcial] = useState("");
  const recRef = useRef(null);
  const cortadoRef = useRef(false);

  const soportado = !!Reconocimiento();

  // Las funciones que llegan por parámetro se guardan en refs para que el
  // reconocedor no haya que recrearlo cada vez que la pantalla se repinta.
  const onTextoRef = useRef(onTexto);
  const onErrorRef = useRef(onError);
  useEffect(() => { onTextoRef.current = onTexto; }, [onTexto]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  const parar = useCallback(() => {
    cortadoRef.current = true;
    try { recRef.current?.stop(); } catch { /* ya estaba parado */ }
    setEscuchando(false);
    setParcial("");
  }, []);

  const escuchar = useCallback(() => {
    const SR = Reconocimiento();
    if (!SR) {
      onErrorRef.current?.("sin-soporte");
      return;
    }
    // Si ya había uno vivo, se cierra: dos reconocedores a la vez se pisan y
    // el navegador acaba lanzando 'aborted'.
    try { recRef.current?.stop(); } catch { /* nada */ }

    const rec = new SR();
    rec.lang = "es-MX";          // le habla a su coach en español
    rec.continuous = false;      // una intervención por pulsación
    rec.interimResults = true;   // para poder ir enseñando lo que oye
    rec.maxAlternatives = 1;

    let final = "";

    rec.onresult = (ev) => {
      let provisional = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const t = ev.results[i][0].transcript;
        if (ev.results[i].isFinal) final += t;
        else provisional += t;
      }
      setParcial(provisional);
      if (final) setParcial(final);
    };

    rec.onerror = (ev) => {
      setEscuchando(false);
      setParcial("");
      // 'aborted' y 'no-speech' no son averías: son que el alumno paró o que
      // no dijo nada. Avisar de eso sería ruido.
      if (ev?.error && ev.error !== "aborted" && ev.error !== "no-speech") {
        onErrorRef.current?.(ev.error);
      }
    };

    rec.onend = () => {
      setEscuchando(false);
      setParcial("");
      const limpio = final.trim();
      // Se entrega solo si hubo algo Y no fue el alumno quien cortó.
      if (limpio && !cortadoRef.current) onTextoRef.current?.(limpio);
      cortadoRef.current = false;
    };

    try {
      cortadoRef.current = false;
      recRef.current = rec;
      rec.start();
      setEscuchando(true);
    } catch {
      setEscuchando(false);
      onErrorRef.current?.("no-arranca");
    }
  }, []);

  // Si se sale de la pantalla con el micrófono abierto, se cierra. Un
  // reconocedor vivo detrás de una página que ya no existe deja el indicador
  // de micrófono encendido en el navegador, y eso asusta con razón.
  useEffect(() => () => {
    try { recRef.current?.abort?.(); } catch { /* nada */ }
  }, []);

  return { soportado, escuchando, parcial, escuchar, parar };
}
